/**
 * Auth Store
 * @typedef {Object} AuthStore
 * @property {boolean} authenticated - Indicates if the user is authenticated.
 * @property {Object} user - User object containing user information.
 * @property {boolean} loadingProfile - Indicates if the user profile is being loaded.
 * @property {string} token - User token.
 * @property {string} refreshToken - Refresh token.
 * @property {number|null} tokenExpires - Expiration time of the user token.
 * @property {number|null} refreshTokenExpires - Expiration time of the refresh token.
 */

/**
 * Auth Store
 * @type {import('pinia').Store<AuthStore>}
 */
import { defineStore } from 'pinia';
import keycloakService from '@services/keycloakService';
import { jwtDecode } from 'jwt-decode';
import SecureLSModule from 'secure-ls';
import logger from '@/utils/logger';

// secure-ls ships a UMD bundle whose CommonJS export is { __esModule: true, default: SecureLS }.
// Vite 5 unwrapped that at runtime; Vite 8 does not, so the default import is the wrapper object.
// Normalize so this works under any bundler interop.
const SecureLS = SecureLSModule?.default ?? SecureLSModule;

// Create an instance of SecureLS with encryption enabled
const ls = new SecureLS({ encodingType: 'aes' });

export const useAuthStore = defineStore({
    id: 'authStore',
    state: () => {
        return {
            authenticated: false,
            user: null,
            loadingProfile: false,
            profileLoaded: false,
            token: '',
            refreshToken: '',
            tokenExpires: null,
            refreshTokenExpires: null,
        };
    },
    getters: {
        /**
         * Check if user has a specific role
         * @param {string} role - Role to check
         * @returns {boolean} True if user has the role
         */
        hasRole: (state) => (role) => {
            return state.user?.roles?.includes(role) || false;
        },
        isAdminUser() {
            return this.user?.isAdminUser || false;
        },
        isStaffUser() {
            return this.user?.isStaffUser || false;
        },
        isNetworkAdequacyUser() {
            return this.user?.isNetworkAdequacyUser || false;
        },
        isQAUser() {
            return this.user?.isQAUser || false;
        },
        isInvestigatorUser() {
            return this.user?.isInvestigatorUser || false;
        },
        isQAHMOUser() {
            return this.user?.isQAHMOUser || false;
        },
        isQADHSUser() {
            return this.user?.isQADHSUser || false;
        },
        isNetAdExternalUser() {
            return this.user?.isNetAdExternalUser || false;
        },
        isHMOReportingUser() {
            return this.user?.isHMOReportingUser || false;
        },
        isHMOLicensingUser() {
            return this.user?.isHMOLicensingUser || false;
        },
        isPBMUser() {
            return this.user?.isPBMUser || false;
        },
        isEntityAdministratorUser() {
            return this.user?.isEntityAdministratorUser || false;
        },
        /**
         * Internal MDH staff roles. These users regulate health care entities rather than
         * belonging to one, so they are not required to hold entity associations.
         */
        isInternalUser() {
            return this.isAdminUser
                || this.isQAUser
                || this.isNetworkAdequacyUser
                || this.isInvestigatorUser;
        },
        isStaffUserByEmail: () => (email) => {
            return String(email ?? '').trim().toLowerCase().endsWith('@state.mn.us');
        },
    },
    persist: {
        storage: {
            getItem: (key) => {
                const data = ls.get(key);
                return data ? JSON.parse(data) : null;
            },
            setItem: (key, value) => {
                ls.set(key, JSON.stringify(value));
            },
            removeItem: (key) => {
                ls.remove(key);
            },
        },
    },
    actions: {
        async initOauth(keycloak) {
            if (this.loadingProfile) {
                return;
            }

            if (keycloak.authenticated) {
                this.loadingProfile = true;
                this.authenticated = keycloak.authenticated;
                if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
                    this.tokenExpires = keycloak.tokenParsed.exp;
                }

                const decodedToken = jwtDecode(keycloak.refreshToken);
                if (decodedToken && decodedToken.exp) {
                    this.refreshTokenExpires = decodedToken.exp;
                }

                this.token = keycloak.token;
                this.refreshToken = keycloak.refreshToken;

                if (this.user == null || !this.profileLoaded) {
                    try {
                        const userProfile = await keycloakService.GetUserProfile();
                        this.user = userProfile;
                        this.profileLoaded = true;
                    } catch (error) {
                        logger.error(error);
                        keycloakService.CallLogout();
                        this.clearUserData();
                    } finally {
                        this.loadingProfile = false;
                    }
                } else {
                    this.profileLoaded = true;
                    this.loadingProfile = false;
                }
            } else {
                this.clearUserData();
                this.loadingProfile = false;
                this.profileLoaded = true;
            }
        },
        async login(redirectUri) {
            try {
                await keycloakService.CallLogin(redirectUri);
                this.clearUserData();
                this.initOauth(keycloakService.keycloak);
            } catch (error) {
                logger.error(error);
            }
        },
        async logout() {
            try {
                this.clearUserData();
                await keycloakService.CallLogout();
                this.router.push({ path: '/' });
            } catch (error) {
                logger.error(error);
            }
        },
        async refreshUserToken() {
            try {
                const keycloak = await keycloakService.CallTokenRefresh();
                await this.initOauth(keycloak);

            } catch (error) {
                logger.error(error);
            }
        },
        /**
         * Forces a new access token and re-reads the profile so newly granted roles take effect
         * without the user having to log out and back in.
         */
        async reloadUserRoles() {
            try {
                const keycloak = await keycloakService.CallForceTokenRefresh();
                if (!keycloak) {
                    return;
                }

                // initOauth only re-reads the profile (and therefore the roles) when it believes
                // the cached profile is stale, so invalidate it first.
                this.profileLoaded = false;
                await this.initOauth(keycloak);
            } catch (error) {
                logger.error(error);
            }
        },
        async checkTokenExpiration() {
            if (this.isTokenExpired()) {
                await this.refreshUserToken();
            }
        },
        isTokenExpired() {
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
            return currentTime >= this.tokenExpires;
        },
        isRefreshTokenExpired() {
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
            return currentTime >= this.refreshTokenExpires;
        },
        clearUserData() {
            this.authenticated = false;
            this.user = null;
            this.profileLoaded = false;
            this.token = '';
            this.refreshToken = '';
            localStorage.clear();
        },
    },
});
